/**
 * JSX components for instruction composition.
 *
 * Components are organized into:
 * 1. Content blocks - reusable patterns for tool guidance, examples, errors
 * 2. Control flow - conditional rendering helpers
 *
 * For dynamic section tags, use JSX intrinsic `<tag-*>` elements directly
 * (e.g., `<tag-identity>`, `<tag-constraints>`). The reconciler handles these natively.
 */

import type { ReactNode } from 'react';

import type { ToolComponent } from '#plugin-chat/lib/tool/create-tool.types.ts';

/** Renders a bold list item for critical/important constraints. */
export function Critical({ children }: { children: ReactNode }) {
  return (
    <li>
      <strong>{children}</strong>
    </li>
  );
}

/** Renders a value as pretty-printed JSON in a fenced code block. */
export function Json({ value }: { value: unknown }) {
  return <pre>{JSON.stringify(value, null, 2)}</pre>;
}

interface ToolGuidanceProps {
  // biome-ignore lint/suspicious/noExplicitAny: Not checked
  tool: ToolComponent<any>;
  description?: string;
  whenToUse: readonly string[];
  whenNotToUse?: readonly string[];
  /** Additional tool-specific guidance rendered after the when-to/not-to sections. */
  children?: ReactNode;
}

/**
 * Reusable block for tool usage guidance with when-to-use/not-to-use patterns.
 *
 * `children` render after the when-to/not-to sections and are intended for
 * tool-specific guidance that doesn't fit the structured when-to-use pattern
 * (procedures, parameter dictionaries, output notes, etc.).
 */
export function ToolGuidance({ tool, description, whenToUse, whenNotToUse, children }: ToolGuidanceProps) {
  return (
    <>
      <h2>{tool.mention} Tool Instructions</h2>
      {description && <p>{description}</p>}
      <h3>When to use {tool.mention}</h3>
      <ul>
        {whenToUse.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {whenNotToUse && whenNotToUse.length > 0 && (
        <>
          <h3>When not to use {tool.mention}</h3>
          <ul>
            {whenNotToUse.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )}
      {children}
    </>
  );
}

interface QueryExampleProps {
  user: string;
  steps: readonly string[];
  name?: string;
}

/**
 * Reusable block for query workflow examples showing user request and assistant steps.
 */
export function QueryExample({ user, steps, name = 'query' }: QueryExampleProps) {
  const stepsText = steps.map((step) => `Assistant: ${step}`).join('\n');
  return <example name={name}>{`User: "${user}"\n${stepsText}`}</example>;
}
